import { defineStore } from 'pinia'
import { useSyncData } from '@/composables/useSyncData'
import type { Customer } from '@/_api/models/customer'

export const useCustomerStore = defineStore('customer', () => {
  const { $api, $grants } = useNuxtApp()
  const authStore = useAuthStore()

  const accountId = computed(() => authStore.activeGuid)

  const {
    items: customers,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    sync: _sync,
    findById,
  } = useSyncData<Customer>({
    service: $api.customer,
    key: 'customers',
    accountId,
    grants: { customer: ['view'] },
  })

  const selected = ref<any[]>([])

  const sync = async (options?: { force?: boolean; change?: boolean }) => {
    if ($grants && !$grants.all({ customer: ['view'] })) {
      return
    }
    await _sync(options)
  }

  const syncDate = computed(() => syncState.value.date)

  const all = computed(() => customers.value)

  // Delegate to the Map-backed findById in useSyncData — O(1) instead of
  // O(n). See .docs/performance.md.
  const getCustomerById = (id: number | string) => findById(id)

  const filtered = ({ filters = null }: { filters?: any } = {}) => {
    if (filters && !filters.isEmpty()) {
      return filters.filter(customers.value)
    }
    return customers.value
  }

  const setSyncState = (patch: Partial<{ running: boolean; progress: number | null; date: Date | null; error: boolean }>) => {
    if (patch.running !== undefined) syncState.value.running = patch.running
    if (patch.progress !== undefined) syncState.value.progress = patch.progress
    if (patch.date !== undefined) syncState.value.date = patch.date
    if (patch.error !== undefined) syncState.value.error = patch.error
  }

  const pageData = ({
    page = 1,
    limit = 100,
    filters = null,
  }: {
    page?: number
    limit?: number
    filters?: any
  } = {}) => {
    if (filters && !filters.isEmpty()) {
      return filtered({ filters }).slice((page - 1) * limit, page * limit)
    }
    return customers.value.slice((page - 1) * limit, page * limit)
  }

  return {
    customers,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    syncDate,
    sync,
    findById,
    all,
    selected,
    getCustomerById,
    filtered,
    setSyncState,
    pageData,
  }
})
